/**
 * GeoJSON (RFC 7946) для таблиць із координатами — чиста логіка, без залежностей від Grist.
 *
 * Навіщо окремий модуль без імпортів. Ті самі правила геометрії виконуються у двох місцях:
 * тут (кнопка «Завантажити як GeoJSON» у редакторі — права й фільтри рахує рушій Grist) і в
 * automation (`app/geojson.py` — публікація з read-model). Щоб вони не розійшлися, обидва
 * проганяються на спільному золотому файлі `test-regional/geojson.golden.json`: Python — у pytest,
 * цей модуль — `node --test` просто з `.ts`, без збірки форку. Тому тут лише стираний
 * TypeScript: інтерфейси й типи, без enum і namespace. [[vyvantazhennia-geojson]]
 *
 * Звідки відомо, де координати: провізія пише в опції колонки `widgetOptions.regionalGeo`
 * (роль «lat»/«lon», шар, для маршрутів — лінія). Схем форк не знає і вгадувати за назвами не
 * мусить.
 *
 * Правила (RFC 7946 і рішення власника Г3, Г6, Г7):
 *  - порядок — `[довгота, широта]` (найчастіша помилка GeoJSON — на неї є тест);
 *  - лише WGS 84, повна точність — число виходить таким, як збережене;
 *  - точка без коректних координат — `geometry: null`, рядок лишається: кількість об'єктів
 *    дорівнює кількості рядків у CSV і JSON. Точка поза рамкою України — теж `null`
 *    (найімовірніше, переплутані поля; причину видно в таблиці як помилку перевірки);
 *  - координата — лише число: текст («50,45») — `null`.
 */

/** Позначка колонки в `widgetOptions.regionalGeo` — її пише `automation/app/geojson.py`. */
export interface RegionalGeoOption {
  role: "lat" | "lon";
  layer?: string;
  layerLabel?: string;
  line?: GeoLineSpec;
}

/** Точки, що складаються в лінію (48/shapes): маршрут, порядок точки, міра довжини. */
export interface GeoLineSpec {
  groupBy: string;
  orderBy: string;
  measure?: string;
}

/** Пара координат таблиці. `key` порожній, якщо пара одна. */
export interface GeoLayer {
  key: string;
  label: string;
  lat: string;
  lon: string;
  line?: GeoLineSpec;
}

export interface GeoColumnInfo {
  colId: string;
  /** Рядок JSON із метаданих документа або вже розібраний об'єкт. */
  widgetOptions: unknown;
}

export type Position = [number, number];

export interface PointGeometry {
  type: "Point";
  coordinates: Position;
}

export interface LineGeometry {
  type: "LineString";
  coordinates: Position[];
}

export interface GeoFeature {
  type: "Feature";
  id?: string | number;
  geometry: PointGeometry | LineGeometry | null;
  properties: Record<string, unknown>;
}

export interface LinePoint {
  group: unknown;
  order: unknown;
  lat: unknown;
  lon: unknown;
  measure?: unknown;
}

/** Рамка України — та сама, що в перевірці (`validation_rules.py`, P16). */
export const UKRAINE_EXTENT = { latMin: 44.0, latMax: 52.5, lonMin: 22.0, lonMax: 40.5 } as const;

export const GEOJSON_MEDIA_TYPE = "application/geo+json";

function parseOptions(value: unknown): Record<string, unknown> | null {
  if (typeof value === "string") {
    if (!value) { return null; }
    try {
      const parsed: unknown = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed as Record<string, unknown> : null;
    } catch {
      return null;
    }
  }
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

function parseLine(value: unknown): GeoLineSpec | undefined {
  const line = parseOptions(value);
  const groupBy = line?.groupBy;
  const orderBy = line?.orderBy;
  const measure = line?.measure;
  if (typeof groupBy !== "string" || typeof orderBy !== "string") {
    return undefined;
  }
  return {
    groupBy,
    orderBy,
    ...(typeof measure === "string" && measure ? { measure } : {}),
  };
}

/** Позначка координати з опцій колонки або `null`. */
export function readGeoOption(widgetOptions: unknown): RegionalGeoOption | null {
  const geo = parseOptions(parseOptions(widgetOptions)?.regionalGeo);
  const role = geo?.role;
  if (!geo || (role !== "lat" && role !== "lon")) {
    return null;
  }
  const layer = geo.layer;
  const layerLabel = geo.layerLabel;
  const line = parseLine(geo.line);
  return {
    role,
    layer: typeof layer === "string" ? layer : "",
    layerLabel: typeof layerLabel === "string" ? layerLabel : "",
    ...(line ? { line } : {}),
  };
}

interface GeoLayerDraft {
  key: string;
  label: string;
  lat?: string;
  lon?: string;
  line?: GeoLineSpec;
}

/** Повні пари координат таблиці, за ключем шару. Неповна пара (лише широта) шаром не стає. */
export function geoLayers(columns: GeoColumnInfo[]): GeoLayer[] {
  const byKey = new Map<string, GeoLayerDraft>();
  for (const column of columns) {
    const option = readGeoOption(column.widgetOptions);
    if (!option) { continue; }
    const key = option.layer || "";
    const layer: GeoLayerDraft = byKey.get(key) || { key, label: option.layerLabel || "" };
    if (option.role === "lat") { layer.lat = column.colId; } else { layer.lon = column.colId; }
    if (option.line) { layer.line = option.line; }
    if (!layer.label && option.layerLabel) { layer.label = option.layerLabel; }
    byKey.set(key, layer);
  }
  const layers: GeoLayer[] = [];
  for (const layer of byKey.values()) {
    if (layer.lat && layer.lon) {
      layers.push({
        key: layer.key, label: layer.label, lat: layer.lat, lon: layer.lon,
        ...(layer.line ? { line: layer.line } : {}),
      });
    }
  }
  return layers.sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
}

/** Усі колонки координат таблиці: у `properties` вони не дублюються. */
export function coordinateColumns(layers: GeoLayer[]): Set<string> {
  const columns = new Set<string>();
  for (const layer of layers) {
    columns.add(layer.lat);
    columns.add(layer.lon);
  }
  return columns;
}

/** Шар для вивантаження: названий — або єдиний. `null` — вибрати не можна. */
export function pickGeoLayer(layers: GeoLayer[], key?: string | null): GeoLayer | null {
  if (key !== undefined && key !== null && key !== "") {
    return layers.find(layer => layer.key === key) || null;
  }
  return layers.length === 1 ? layers[0] : null;
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function pointGeometry(lat: unknown, lon: unknown): PointGeometry | null {
  const la = finiteNumber(lat);
  const lo = finiteNumber(lon);
  if (la === null || lo === null) { return null; }
  const e = UKRAINE_EXTENT;
  if (la < e.latMin || la > e.latMax || lo < e.lonMin || lo > e.lonMax) { return null; }
  return { type: "Point", coordinates: [lo, la] };
}

function featureId(value: unknown): string | number | undefined {
  if (typeof value === "string") { return value === "" ? undefined : value; }
  if (typeof value === "number" && Number.isFinite(value)) { return value; }
  return undefined;
}

export function pointFeature(
  id: unknown, lat: unknown, lon: unknown, properties: Record<string, unknown>,
): GeoFeature {
  const fid = featureId(id);
  return {
    type: "Feature",
    ...(fid !== undefined ? { id: fid } : {}),
    geometry: pointGeometry(lat, lon),
    properties,
  };
}

/**
 * Один `Feature` з `LineString` на маршрут. Маршрути — у порядку першої появи (як людина бачить
 * рядки), точки — за `orderBy`; рівний порядок лишає вихідний. Менше двох коректних точок —
 * `geometry: null`. Точка без маршруту не належить жодній лінії й не виходить.
 */
export function lineFeatures(line: GeoLineSpec, points: Iterable<LinePoint>): GeoFeature[] {
  const groups = new Map<string | number, LinePoint[]>();
  for (const point of points) {
    const group = featureId(point.group);
    if (group === undefined) { continue; }
    const members = groups.get(group);
    if (members) { members.push(point); } else { groups.set(group, [point]); }
  }
  const features: GeoFeature[] = [];
  for (const [group, members] of groups) {
    const ordered = members
      .filter(p => finiteNumber(p.order) !== null && pointGeometry(p.lat, p.lon) !== null)
      .sort((a, b) => (finiteNumber(a.order) as number) - (finiteNumber(b.order) as number));
    const coordinates: Position[] = ordered.map(p => [p.lon as number, p.lat as number]);
    const measures = members.map(p => finiteNumber(p.measure)).filter((m): m is number => m !== null);
    const properties: Record<string, unknown> = { [line.groupBy]: group, pointCount: members.length };
    if (line.measure) {
      // reduce, а не Math.max(...): маршрут GTFS буває на десятки тисяч точок — розгорнуті
      // аргументи переповнили б стек.
      properties[line.measure] = measures.length ? measures.reduce((a, b) => (b > a ? b : a)) : null;
    }
    features.push({
      type: "Feature",
      id: group,
      geometry: coordinates.length >= 2 ? { type: "LineString", coordinates } : null,
      properties,
    });
  }
  return features;
}

/**
 * Потоковий FeatureCollection: частини рядка, які можна писати у відповідь по одній, не будуючи
 * файл на десятки тисяч рядків у пам'яті цілком.
 */
export function* featureCollectionChunks(features: Iterable<GeoFeature>): Generator<string> {
  yield "{\"type\":\"FeatureCollection\",\"features\":[";
  let first = true;
  for (const feature of features) {
    yield (first ? "" : ",") + JSON.stringify(feature);
    first = false;
  }
  yield "]}";
}

// --- Вибір точки на мапі ------------------------------------------------------------------------
// Правила діалогу «Точка на мапі» (`app/client/ui/RegionalGeoPicker.ts`) — тут, бо перевіряються
// `node --test` без збірки форку. [[koordynaty-tochkoiu-na-mapi]]

export interface PickerPoint {
  lat: number;
  lon: number;
}

/** З чого почати показ мапи: точка рядка, місцевість за іншими рядками таблиці або вся країна. */
export type PickerStartView =
  { kind: "point"; lat: number; lon: number } |
  { kind: "area"; lat: number; lon: number } |
  { kind: "country" };

/** Пара координат, до якої належить колонка. `null` — колонка не координата чи пара неповна. */
export function layerOfColumn(layers: GeoLayer[], colId: string): GeoLayer | null {
  return layers.find(layer => layer.lat === colId || layer.lon === colId) || null;
}

/** Точка для запису в рядок, `null` — поза рамкою України чи не число. Шість знаків — це близько
 *  10 см: точніше кліком не вкажеш, а хвости на кшталт 50.74720000000001 лише псують дані. */
export function pickerPoint(lat: unknown, lon: unknown): PickerPoint | null {
  const point = pointGeometry(lat, lon);
  return point && {
    lat: Number(point.coordinates[1].toFixed(6)),
    lon: Number(point.coordinates[0].toFixed(6)),
  };
}

/** Число з поля діалогу: у таблицях звичні й кома, й крапка. `null` — порожньо чи не число. */
export function coordinateFromText(text: string): number | null {
  const normalized = text.trim().replace(",", ".");
  if (!/^[+-]?(\d+\.?\d*|\.\d+)$/.test(normalized)) { return null; }
  return Number(normalized);
}

function median(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/**
 * Початковий вигляд мапи. Місцевість — медіана точок таблиці, а не рамка навколо них: одна
 * помилкова координата на іншому кінці країни інакше показала б людині всю Україну.
 */
export function pickerStartView(current: PickerPoint | null, others: PickerPoint[]): PickerStartView {
  if (current) { return { kind: "point", lat: current.lat, lon: current.lon }; }
  if (others.length === 0) { return { kind: "country" }; }
  return { kind: "area", lat: median(others.map(p => p.lat)), lon: median(others.map(p => p.lon)) };
}
